import { useCallback, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router';
import {
  Button,
  Field,
  Icon,
  Input,
  LINK_CLASS,
  Link,
  Notice,
  Csel,
  Check,
  Reason,
} from '@efira/ui';
import { deliverabilityWarning, fieldErrors, signUpSchema } from '@efira/contracts';

import { Turnstile } from '../auth/Turnstile';
import { TURNSTILE_SITE_KEY } from '../config';
import { isRateLimited, reasonFrom } from '../api/reason';

import { authClient } from '../auth/client';
import { detectTimezone, timeInZone, timezoneOptions } from '../lib/timezones';
import { AuthLayout } from './AuthLayout';
import { SocialButtons } from './SocialButtons';

/**
 * Регистрация.
 *
 * ПОВЕДЕНИЕ ПРИ ЗАНЯТОМ АДРЕСЕ. Сервер отвечает успехом и на занятый адрес -
 * иначе по ответу перебираются зарегистрированные почты. Экран обязан вести
 * себя ОДИНАКОВО в обоих случаях и уходит на «письмо отправлено». Различие
 * живёт в письме: новому адресу - ссылка подтверждения, занятому -
 * уведомление со ссылками на вход и восстановление.
 *
 * Поэтому здесь нет и не должно появиться ветки «этот email уже занят».
 *
 * TikTok-username не спрашивается: он отдельный пропускаемый шаг и не должен
 * блокировать вход (docs/SPEC.md, 2.3). В этом блоке шага нет вовсе.
 */
export function SignUpScreen() {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [timezone, setTimezone] = useState(detectTimezone);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [checkFailed, setCheckFailed] = useState(false);
  /* Стабильная ссылка: виджет пересоздаётся при смене обработчиков. */
  const onCheckFailed = useCallback(() => {
    setCheckFailed(true);
  }, []);

  /**
   * Предупреждение о домене с плохой доставляемостью. СЕЙЧАС НЕ ПОКАЗЫВАЕТСЯ.
   *
   * Список доменов в `@efira/contracts` ПУСТ с 2026-09-16, поэтому функция
   * всегда возвращает null и предупреждение не рисуется. Замер, на котором оно
   * держалось, отменён: он делался с отправителя замороженного проекта через
   * Supabase, а с Resend письма доходят и до mail.ru (`DECISIONS.md`, запись
   * [2026-09-16]).
   *
   * ВЫЗОВ ОСТАВЛЕН НАМЕРЕННО. Доставляемость меняется сама, без единой правки
   * у нас. Пока проводка на месте, возврат предупреждения - одна строка в
   * списке доменов; выдранный отсюда вызов пришлось бы писать заново, а
   * комментарий в `contracts` про «что включает его обратно» стал бы
   * неправдой.
   *
   * ПРЕДУПРЕЖДЕНИЕ, А НЕ ЗАПРЕТ - это правило остаётся в силе на случай
   * возврата: форма отправляется как обычно.
   */
  const emailWarning = useMemo(() => deliverabilityWarning(email), [email]);

  /*
   * ПОКА ФОРМА НЕ ГОТОВА - «СОЗДАТЬ АККАУНТ» НЕ ГЛАВНАЯ, И СКАЗАНО ПОЧЕМУ.
   * Находка N-18 повторного аудита: кнопка была залита при пустой форме,
   * неотмеченном согласии и непройденной проверке - человек жал и получал
   * ошибки вместо аккаунта. Причина одна, первая по порядку формы, и
   * сказана именно она: не отмечено согласие - так и написано.
   *
   * Строгая проверка (формат почты, длина имени) остаётся за схемой при
   * отправке: здесь - только то, без чего отправлять бессмысленно.
   */
  const notReady =
    name.trim() === ''
      ? 'Впишите имя — его увидят зрители.'
      : email.trim() === ''
        ? 'Впишите почту — на неё придёт ссылка подтверждения.'
        : password.length < 8
          ? 'Придумайте пароль — от 8 символов.'
          : !acceptTerms
            ? 'Отметьте согласие с условиями использования.'
            : TURNSTILE_SITE_KEY !== undefined && turnstileToken === null && !checkFailed
              ? 'Дождитесь проверки Cloudflare над кнопкой.'
              : null;

  const zones = useMemo(
    () =>
      timezoneOptions().map((zone) => ({
        value: zone.value,
        label: zone.label,
        hint: `сейчас ${timeInZone(zone.value)}`,
      })),
    [],
  );

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);

    const parsed = signUpSchema.safeParse({
      name,
      email,
      password,
      timezone,
      acceptTerms,
    });

    if (!parsed.success) {
      setErrors(fieldErrors(parsed.error));
      return;
    }

    setErrors({});
    setLoading(true);

    const { error } = await authClient.signUp.email(
      {
        name: parsed.data.name,
        email: parsed.data.email,
        password: parsed.data.password,
        // Пояс уезжает вместе с регистрацией: от него зависит момент сброса квоты.
        timezone: parsed.data.timezone,
      },
      {
        // Токен Turnstile идёт ЗАГОЛОВКОМ: тело этого запроса разбирает
        // Better Auth по своей схеме и посторонние поля отбрасывает.
        // Без токена боевой сервер отклоняет регистрацию - ключ там
        // обязателен, и до этой правки виджета не существовало вовсе.
        ...(turnstileToken === null ? {} : { headers: { 'x-turnstile-token': turnstileToken } }),
      },
    );

    setLoading(false);

    if (error !== null && error !== undefined) {
      // Сюда попадают только настоящие сбои: сеть, 429, отказ Turnstile.
      // Занятость адреса сюда не приходит и прийти не должна.
      //
      // ПРИЧИНА СЕРВЕРА ПОКАЗЫВАЕТСЯ, ЕСЛИ ОНА ЕСТЬ. Общий текст стоял здесь
      // безусловно, и отказ Turnstile читался как «не удалось отправить
      // письмо» - час диагностики почты, с которой всё было в порядке.
      setFormError(
        isRateLimited(error)
          ? 'Слишком много попыток подряд. Подождите несколько минут.'
          : reasonFrom(
              error,
              'Не удалось отправить письмо. Проверьте соединение и попробуйте ещё раз.',
            ),
      );
      return;
    }

    navigate('/check-email', { replace: true, state: { email: parsed.data.email } });
  }

  return (
    <AuthLayout
      title="Регистрация"
      subtitle="Ссылка оверлея выдаётся сразу после подтверждения почты и не меняется между эфирами"
      footer={
        <>
          Уже есть аккаунт?{' '}
          <RouterLink className={LINK_CLASS} to="/login">
            Войти
          </RouterLink>
        </>
      }
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError !== null ? (
          <div>
            <Notice tone="error" title="Не получилось" live>
              {formError}
            </Notice>
          </div>
        ) : null}

        {/*
          ПОМЕТОК ОБЯЗАТЕЛЬНОСТИ НЕТ НИ У ОДНОГО ПОЛЯ (N-17). Звёздочки
          стояли у двух полей из трёх обязательных органов и не стояли у
          согласия - пометка не у всех обязательных не сообщает ничего.
          Выбрано «ни одной», как на входе: чего не хватает, называет
          причина у кнопки отправки. Атрибут `required` у полей остаётся.
          Ворота: `e2e/class-forms.spec.ts` - пометок ноль или столько же,
          сколько обязательных органов, на каждой форме продукта.
        */}
        <Field label="Как вас зовут" error={errors['name']} required requiredMark={false}>
          {(props) => (
            <Input
              {...props}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
              }}
              autoComplete="name"
              autoFocus
              placeholder="Имя на стриме"
            />
          )}
        </Field>

        <Field
          label="Почта"
          hint="На неё придёт ссылка подтверждения"
          error={errors['email']}
          required
          requiredMark={false}
        >
          {(props) => (
            <Input
              {...props}
              type="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
            />
          )}
        </Field>

        {/* Предупреждение стоит ПОД полем и не блокирует отправку: это
            сообщение о риске, а не ошибка ввода. Поэтому Notice, а не
            `error` поля — у ошибки поля другая роль и другой цвет. */}
        {emailWarning !== null ? (
          <div className="-mt-2">
            <Notice tone="warning" title="Проверьте адрес" live="polite">
              {emailWarning}
            </Notice>
          </div>
        ) : null}

        <Field
          label="Пароль"
          hint="Не короче 8 символов"
          error={errors['password']}
          required
          requiredMark={false}
        >
          {(props) => (
            <Input
              {...props}
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              autoComplete="new-password"
            />
          )}
        </Field>

        <Field
          label="Часовой пояс"
          hint="По нему сбрасывается дневная квота эфира — чтобы она не обнулялась посреди ночи"
          error={errors['timezone']}
        >
          {(props) => (
            <Csel
              id={props.id}
              aria-describedby={props['aria-describedby']}
              aria-invalid={props['aria-invalid']}
              value={timezone}
              options={zones}
              onChange={setTimezone}
              searchPlaceholder="Город или регион"
            />
          )}
        </Field>

        <div className="flex flex-col gap-2">
          <Check
            checked={acceptTerms}
            onChange={setAcceptTerms}
            aria-invalid={errors['acceptTerms'] !== undefined}
            label="Принимаю условия использования"
            hint="Публичная оферта и политика обработки данных"
          />
          {errors['acceptTerms'] !== undefined ? (
            <span className="text-destructive inline-flex items-start gap-1 text-xs" role="alert">
              <Icon name="error" size="sm" className="mt-1" />
              <span>{errors['acceptTerms']}</span>
            </span>
          ) : null}
        </div>

        {/* Виджет стоит перед кнопкой, а не после: проверка должна быть
            пройдена до нажатия, иначе отказ приходит уже после отправки формы
            и выглядит как её сбой. Без публичного ключа не рисуется вовсе -
            это состояние разработки. */}
        <Turnstile onToken={setTurnstileToken} onFailed={onCheckFailed} />

        <div className="mt-2 flex flex-col gap-3">
          <Button
            type="submit"
            variant="primary"
            block
            loading={loading}
            disabled={notReady !== null}
            aria-describedby={notReady === null ? undefined : 'signup-why'}
          >
            Создать аккаунт
          </Button>
          {notReady !== null && (
            <Reason id="signup-why" data-auth="signup-why">
              {notReady}
            </Reason>
          )}

          <SocialButtons />
        </div>
      </form>

      <p className="text-foreground/75 mt-4 text-sm">
        TikTok-аккаунт спросим позже, отдельным шагом. Он не нужен, чтобы начать.{' '}
        <Link href="https://efira.live" external quiet>
          О продукте
        </Link>
      </p>
    </AuthLayout>
  );
}
